import type { Action, ActionWithContext } from './types';

const ACTION_EVENT_NAME = 'action';

export class ActionBus extends EventTarget {
  private static instance: ActionBus | null = null;
  private static readonly token: unique symbol = Symbol('ActionBus');

  /**
   * In-process action bus built on `EventTarget`.
   * Uses a single `action` event carrying `ActionWithContext` in `detail`.
   * Access via `getInstance()`, emit via `emit()`, subscribe via `on()`.
   */
  private constructor(token: typeof ActionBus.token) {
    super();
    if (token !== ActionBus.token) {
      throw new Error('ActionBus: use ActionBus.getInstance()');
    }
  }

  /**
   * Returns the singleton instance, creating it if needed.
   *
   * @returns The shared `ActionBus` instance.
   */
  static getInstance(): ActionBus {
    if (!ActionBus.instance) {
      ActionBus.instance = new ActionBus(ActionBus.token);
    }
    return ActionBus.instance;
  }

  /**
   * Emits an action with optional bound context on the `action` event.
   *
   * @param action - The action to emit.
   * @param boundContext - Optional context attached to the emitted action.
   */
  emit(action: Action, boundContext: unknown = {}) {
    this.dispatchEvent(
      new CustomEvent<ActionWithContext>(ACTION_EVENT_NAME, {
        detail: {
          ...action,
          context: boundContext,
        },
      })
    );
  }

  /**
   * Subscribes to actions emitted by the bus.
   *
   * @param listener - Callback invoked with the prepared action.
   * @returns Unsubscribe function.
   */
  on(listener: (action: ActionWithContext) => void) {
    const wrapped = (e: Event) =>
      listener((e as CustomEvent<ActionWithContext>).detail);
    this.addEventListener(ACTION_EVENT_NAME, wrapped);
    return () => this.removeEventListener(ACTION_EVENT_NAME, wrapped);
  }
}
